/**
 * Customers Management Page
 * Table with search, pagination, and toggle active status
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Search,
  ToggleLeft,
  ToggleRight,
  ChevronLeft,
  ChevronRight,
  Trash2,
  ChevronDown,
  FileDown,
} from 'lucide-react';
import { Listbox } from '@headlessui/react';
import { realAPI } from '../api/realEndpoints';
import { adaptRealUser } from '../api/realTypes';
import { useToastNotifications } from '../hooks/useToast';
import { debounce, formatDate } from '../utils/helpers';
import { getAvatarBackgroundColor, getUserImageSrc, getUserInitial } from '../utils/userImage';
import { ITEMS_PER_PAGE } from '../utils/constants';
import type { Customer } from '../api/types';
import Card from '../components/Card';
import Button from '../components/Button';
import Modal from '../components/Modal';
import './ContentManagement.css';

export default function Customers() {
  const { success, error } = useToastNotifications();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [total, setTotal] = useState(0);
  const [deletedTotal, setDeletedTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [isLoading, setIsLoading] = useState(true);
  const [restoringId, setRestoringId] = useState<string | number | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  // Sorting functionality (including registration date)
  const [sortField, setSortField] = useState<'name' | 'email' | 'status' | 'joinedAt' | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Status filter options
  const statusOptions = [
    { label: 'All Status', value: 'all' },
    { label: 'Active', value: 'active', color: 'text-green-600' },
    { label: 'Inactive', value: 'inactive', color: 'text-red-600' },
  ];

  // State for delete confirmation modal
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState<Customer | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRestoreModalOpen, setIsRestoreModalOpen] = useState(false);
  const [customerToRestore, setCustomerToRestore] = useState<Customer | null>(null);
  const showDeleted = searchParams.get('view') === 'deleted';

  const fetchCustomers = useCallback(async () => {
    setIsLoading(true);
    try {
      const statusParam = !showDeleted && statusFilter !== 'all' ? statusFilter : undefined;
      const searchParam = showDeleted ? undefined : search;
      const shortBy = sortField === 'joinedAt' ? 'createdAt' : sortField || undefined;
      const shortType = sortOrder || undefined;

      const data = await realAPI.users.getList(
        page,
        ITEMS_PER_PAGE,
        searchParam,
        statusParam,
        shortBy,
        shortType,
        showDeleted
      );

      if (!data) {
        setCustomers([]);
        setTotal(0);
        return;
      }

      const adaptedCustomers = data.results.map(adaptRealUser);

      setCustomers(adaptedCustomers);
      setTotal(data.count);
      setDeletedTotal(data.deletedCount || 0);
    } catch (error: any) {
      console.error('Failed to fetch customers:', error);
      if (error.message?.includes('404') || error.status === 404) {
        setCustomers([]);
        setTotal(0);
      }
    } finally {
      setIsLoading(false);
    }
  }, [page, search, showDeleted, statusFilter, sortField, sortOrder]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const debouncedSearch = useCallback(
    debounce((value: string) => {
      setSearch(value);
      setPage(1);
    }, 300),
    []
  );

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    debouncedSearch(value);
  };

  const handleToggleStatus = (customer: Customer) => {
    const newStatus: 'active' | 'inactive' = customer.status === 'active' ? 'inactive' : 'active';

    // 1. Show immediate success feedback
    success(`Customer status updated to ${newStatus}`, 'Status changed successfully');

    // 2. Instantly update UI
    setCustomers((prev) =>
      prev.map((c) => (c.id === customer.id ? { ...c, status: newStatus } : c))
    );

    // 3. Background API call (silent)
    realAPI.users.updateUser(customer.id, { status: newStatus }).catch((err) => {
      console.error('Failed to update customer status:', err);
      error('Failed to update status on server', 'The change has been reverted');

      // 4. Revert UI on error
      setCustomers((prev) =>
        prev.map((c) => (c.id === customer.id ? { ...c, status: customer.status } : c))
      );
    });
  };

  const handleViewCustomer = (customer: Customer) => {
    navigate(`/customers/${customer.id}${showDeleted ? '?view=deleted' : ''}`);
  };

  const handleToggleDeletedView = () => {
    const params = new URLSearchParams(searchParams);
    if (showDeleted) {
      params.delete('view');
    } else {
      params.set('view', 'deleted');
    }
    setSearchParams(params);
    setPage(1);
    setSearch('');
    setSearchInput('');
    setStatusFilter('all');
  };

  const handleGoToCustomers = () => {
    const params = new URLSearchParams(searchParams);
    params.delete('view');
    setSearchParams(params);
    setPage(1);
  };

  const handleDeleteCustomer = (customer: Customer) => {
    setCustomerToDelete(customer);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!customerToDelete) return;

    setIsDeleting(true);

    try {
      await realAPI.users.deleteUser(customerToDelete.id);
      await fetchCustomers();
      setIsDeleteModalOpen(false);
      setCustomerToDelete(null);
    } catch (error) {
      console.error('Failed to delete customer:', error);
      alert('Failed to delete customer. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRestoreCustomer = async (customer: Customer) => {
    setRestoringId(customer.id);
    try {
      await realAPI.users.restoreUser(customer.id);
      success('Customer restored successfully');
      setCustomers((prev) => prev.filter((c) => c.id !== customer.id));
      setTotal((prev) => Math.max(0, prev - 1));
      setDeletedTotal((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to restore customer:', err);
      error('Failed to restore customer', 'Please try again.');
    } finally {
      setRestoringId(null);
    }
  };

  const handleOpenRestoreModal = (customer: Customer) => {
    setCustomerToRestore(customer);
    setIsRestoreModalOpen(true);
  };

  const handleConfirmRestore = async () => {
    if (!customerToRestore) return;
    await handleRestoreCustomer(customerToRestore);
    setIsRestoreModalOpen(false);
    setCustomerToRestore(null);
  };

  const totalPages = Math.ceil(total / ITEMS_PER_PAGE);

  /* ------------------------------ EXPORT CUSTOMERS ------------------------------ */
  const handleExportCustomers = async () => {
    try {
      setIsExporting(true);
      const blob = await realAPI.users.exportCustomers();
      if (!blob) return;

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'customers.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      success('Customers exported successfully', 'The CSV file has been downloaded');
    } catch (err) {
      console.error('Failed to export customers:', err);
      error('Failed to export customers', 'Please try again later');
    } finally {
      setIsExporting(false);
    }
  };

  const CUSTOMERS_TABLE_GRID = 'grid-cols-[2.6fr,3fr,1.5fr,1.15fr,0.95fr,1.15fr]';

  // Table shimmer component
  const TableShimmer = () => (
    <div className="table-container">
      <div className="table-min-w">
        {/* Real Table Headers without sorting icons */}
        <div className={`grid ${CUSTOMERS_TABLE_GRID} gap-3 p-4 pl-3 rounded-t-xl font-semibold text-gray-700 text-[15px] border-b border-gray-200 -ml-2 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 dark:text-white dark:border-gray-600 shadow-sm`}>
          <div className="text-gray-700 dark:text-white">Name</div>
          <div className="text-gray-700 dark:text-white">Email</div>
          <div className="text-gray-700 dark:text-white">Mobile Number</div>
          <div className="text-gray-700 dark:text-white">Registration Date</div>
          <div className="text-gray-700 dark:text-white text-center">Status</div>
          <div className="text-gray-700 dark:text-white text-center">Actions</div>
        </div>

        {/* Loader in table area */}
        <div className="flex flex-col items-center justify-center py-12">
          <div className="loader"></div>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <Card className="mb-2 p-[10px]">
        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-2xl font-bold text-gray-900 mb-4 sm:mb-0 dark:text-white">
            Customers
          </h1>

          <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
            {/* Deleted Customers Toggle */}

            {!showDeleted && (
              <div className="flex items-center">
                <Button
                  variant="outline"
                  size="sm"
                  leftIcon={<FileDown className="w-4 h-4" />}
                  onClick={handleExportCustomers}
                  disabled={isExporting}
                >
                  {isExporting ? 'Exporting...' : 'Export Customers'}
                </Button>
              </div>
            )}

            <div className="flex items-center">
              <button
                onClick={handleToggleDeletedView}
                className="flex items-center gap-3 px-6 h-10 rounded-xl text-base font-medium shadow-sm w-full sm:w-auto justify-center transition-colors bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
              >
                <Trash2 className="w-4 h-4" />
                <span>Deleted Customers : {deletedTotal}</span>
              </button>
            </div>

            {showDeleted && (
              <Button variant="secondary" onClick={handleGoToCustomers} className="px-4 h-10">
                Go to Customers
              </Button>
            )}

            {!showDeleted && (
              <div className="flex items-center">
                <div
                  className="flex items-center gap-3 bg-gray-100 px-6 h-10
        rounded-xl text-gray-800 text-base font-medium 
        shadow-sm w-full sm:w-auto justify-center dark:bg-gray-700 dark:text-white"
                >
                  <span>Total Customers : {total}</span>
                </div>
              </div>
            )}

            {!showDeleted && (
              <>
                {/* Search */}
                <div className="relative w-full sm:w-64 h-10">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search by name or email"
                    value={searchInput}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    className="w-full h-full pl-10 pr-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                {/* Status Filter */}
                <div
                  className={`relative flex-shrink-0 transition-all duration-200
    ${!statusFilter || statusFilter === 'all' ? 'w-[140px]' : 'w-[162px]'}`}
                >
                  <Listbox
                    value={statusFilter}
                    onChange={(v) => {
                      setStatusFilter(v as 'all' | 'active' | 'inactive');
                      setPage(1);
                    }}
                  >
                    <Listbox.Button
                      className="w-full h-10 pl-4 pr-10 text-left bg-white border border-gray-300 
      rounded-lg shadow-sm focus:outline-none focus:ring-2 
      focus:ring-primary-500 transition-all duration-200 hover:border-gray-400 
      cursor-pointer flex items-center overflow-hidden"
                    >
                      <span className="block truncate">
                        {statusOptions.find((o) => o.value === statusFilter)?.label || 'Status'}
                      </span>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    </Listbox.Button>

                    <Listbox.Options className="absolute z-20 mt-2 w-full bg-white rounded-lg shadow-lg border border-gray-200">
                      {statusOptions.map((option) => (
                        <Listbox.Option
                          key={option.value}
                          value={option.value}
                          className={({ active }) =>
                            `px-4 py-2 cursor-pointer transition-colors ${active ? 'bg-gray-100' : ''}`
                          }
                        >
                          <span className={option.color}>{option.label}</span>
                        </Listbox.Option>
                      ))}
                    </Listbox.Options>
                  </Listbox>
                </div>

                {/* Reset Filters */}
                <Button
                  variant="secondary"
                  onClick={() => {
                    setStatusFilter('all');
                    setSortField('joinedAt');
                    setSortOrder('desc');
                    setSearch('');
                    setSearchInput('');
                    setPage(1);
                    success('All filters reset', 'Filters have been cleared');
                  }}
                  className="px-4 h-10"
                  style={{ width: '162px' }}
                >
                  Reset Filters
                </Button>
              </>
            )}
          </div>
        </div>
      </Card>

      <Card>
        {isLoading ? (
          <TableShimmer />
        ) : customers.length === 0 ? (
          <div className="table-container">
            <div className="table-min-w">
              {/* Grid Header */}
              <div
                className={`grid ${CUSTOMERS_TABLE_GRID} gap-3 p-4 pl-3 rounded-t-xl font-semibold text-gray-700 text-[15px] border-b border-gray-200 -ml-2 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 dark:text-white dark:border-gray-600 shadow-sm`}
              >
                <div
                  className="cursor-pointer hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white"
                  onClick={() => {
                    if (sortField === 'name') {
                      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                    } else {
                      setSortField('name');
                      setSortOrder('asc');
                    }
                    setPage(1);
                  }}
                >
                  Name {sortField === 'name' && (sortOrder === 'asc' ? '↑' : '↓')}
                </div>

                <div
                  className="cursor-pointer hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white"
                  onClick={() => {
                    if (sortField === 'email') {
                      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                    } else {
                      setSortField('email');
                      setSortOrder('asc');
                    }
                    setPage(1);
                  }}
                >
                  Email {sortField === 'email' && (sortOrder === 'asc' ? '↑' : '↓')}
                </div>

                <div>Mobile Number</div>

                <div
                  className="cursor-pointer hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white"
                  onClick={() => {
                    if (sortField === 'joinedAt') {
                      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                    } else {
                      setSortField('joinedAt');
                      setSortOrder('desc');
                    }
                    setPage(1);
                  }}
                >
                  Registration Date {sortField === 'joinedAt' && (sortOrder === 'asc' ? '↑' : '↓')}
                </div>

                <div
                  className="cursor-pointer text-center hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white"
                  onClick={() => {
                    if (sortField === 'status') {
                      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                    } else {
                      setSortField('status');
                      setSortOrder('asc');
                    }
                    setPage(1);
                  }}
                >
                  Status {sortField === 'status' && (sortOrder === 'asc' ? '↑' : '↓')}
                </div>
                <div className="text-center dark:text-white">Actions</div>
              </div>
              {/* Empty State inside table */}
              <div className="text-center py-12 bg-gray-50 rounded-b-xl">
                <img src="/NoRecord.svg" alt="No records found" className=" mx-auto mb-4 " />
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="table-container">
              <div className="table-min-w">
                {/* Grid Header */}
                <div
                  className={`grid ${CUSTOMERS_TABLE_GRID} gap-3 p-4 pl-3 rounded-t-xl font-semibold text-gray-700 text-[15px] border-b border-gray-200 -ml-2 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 dark:text-white dark:border-gray-600 shadow-sm`}
                >
                  <div
                    className="cursor-pointer hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white"
                    onClick={() => {
                      if (sortField === 'name') {
                        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                      } else {
                        setSortField('name');
                        setSortOrder('asc');
                      }
                      setPage(1);
                    }}
                  >
                    Name {sortField === 'name' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </div>

                  <div
                    className="cursor-pointer hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white"
                    onClick={() => {
                      if (sortField === 'email') {
                        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                      } else {
                        setSortField('email');
                        setSortOrder('asc');
                      }
                      setPage(1);
                    }}
                  >
                    Email {sortField === 'email' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </div>

                  <div>Mobile Number</div>

                  <div
                    className="cursor-pointer hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white"
                    onClick={() => {
                      if (sortField === 'joinedAt') {
                        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                      } else {
                        setSortField('joinedAt');
                        setSortOrder('desc');
                      }
                      setPage(1);
                    }}
                  >
                    Registration Date{' '}
                    {sortField === 'joinedAt' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </div>

                  <div
                    className="cursor-pointer text-center hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white"
                    onClick={() => {
                      if (sortField === 'status') {
                        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                      } else {
                        setSortField('status');
                        setSortOrder('asc');
                      }
                      setPage(1);
                    }}
                  >
                    Status {sortField === 'status' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </div>

                  <div className="text-center dark:text-white">Actions</div>
                </div>

                {/* Grid Body */}
                <div className="divide-y divide-gray-200 dark:divide-gray-700 -ml-2">
                  {customers.map((customer) => {
                    const customerImageSrc = getUserImageSrc(
                      customer.image,
                      customer.signInProvider
                    );

                    return (
                      <div
                        key={customer.id}
                        className={`grid ${CUSTOMERS_TABLE_GRID} gap-3 p-2 pl-2 hover:bg-blue-50 dark:hover:bg-gray-700/50 transition-colors items-center cursor-pointer`}
                        onClick={() => handleViewCustomer(customer)}
                      >
                        <div className="flex items-center gap-2 truncate">
                          {customerImageSrc ? (
                            <img
                              src={customerImageSrc}
                              alt={customer.name}
                              className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                              onError={(e) => {
                                console.warn('Customer image failed to load:', customer.image);
                                e.currentTarget.src = '/logo192.png';
                              }}
                              onLoad={() => { }}
                            />
                          ) : (
                            <div
                              className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 text-white font-bold text-xl"
                              style={{ backgroundColor: getAvatarBackgroundColor(customer.name) }}
                            >
                              {getUserInitial(customer.name, 'U')}
                            </div>
                          )}
                          <span
                            className="capitalize truncate dark:text-white"
                            title={customer.name}
                          >
                            {customer.name || 'Unknown'}
                          </span>
                        </div>

                        {/* Email (bigger now) */}
                        <div
                          className="truncate text-sm text-black-600 dark:text-gray-300"
                          title={customer.email}
                        >
                          {customer.email}
                        </div>

                        <div className="truncate text-sm text-black-600 dark:text-gray-300">
                          {customer.phone || 'N/A'}
                        </div>

                        <div className="text-sm text-black-600 dark:text-gray-300">
                          {customer.joinedAt ? formatDate(customer.joinedAt) : 'N/A'}
                        </div>

                        <div className="flex justify-center">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-md capitalize ${customer.status === 'active'
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                              }`}
                          >
                            {customer.status}
                          </span>
                        </div>

                        <div>
                          <div className="flex justify-center gap-3">
                            {showDeleted ? (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenRestoreModal(customer);
                                }}
                                disabled={restoringId === customer.id}
                                className="px-3 py-1 text-sm rounded-md bg-green-100 text-green-700 hover:bg-green-200 disabled:opacity-60"
                              >
                                {restoringId === customer.id ? 'Restoring...' : 'Restore'}
                              </button>
                            ) : (
                              <>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleToggleStatus(customer);
                                  }}
                                  className={`p-2 rounded-lg transition-colors ${customer.status === 'active'
                                      ? 'text-green-600 hover:bg-green-50'
                                      : 'text-red-600 hover:bg-red-50'
                                    }`}
                                >
                                  {customer.status === 'active' ? (
                                    <ToggleRight className="w-7 h-7" />
                                  ) : (
                                    <ToggleLeft className="w-7 h-7" />
                                  )}
                                </button>

                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteCustomer(customer);
                                  }}
                                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                >
                                  <Trash2 className="w-6 h-6" />
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-6 pt-6 border-t border-gray-200 dark:border-gray-600">
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Showing {(page - 1) * ITEMS_PER_PAGE + 1} to{' '}
                  {Math.min(page * ITEMS_PER_PAGE, total)} of {total} results
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="px-4 py-2 text-sm font-medium text-gray-700">
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setPage(page + 1)}
                    disabled={page === totalPages}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setCustomerToDelete(null);
        }}
        title="Delete Customer"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-700">
            Are you sure you want to delete this customer? This action cannot be undone.
          </p>
          <div className="bg-red-50 p-3 rounded-lg">
            <p className="text-sm text-red-800">
              <strong>Warning:</strong> Deleting this customer will permanently remove all their
              data, including booking history and reviews.
            </p>
          </div>
          <div className="flex gap-3 justify-end">
            <Button
              variant="secondary"
              onClick={() => {
                setIsDeleteModalOpen(false);
                setCustomerToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button variant="danger" onClick={handleConfirmDelete} disabled={isDeleting}>
              {isDeleting ? 'Deleting' : 'Delete Customer'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Restore Confirmation Modal */}
      <Modal
        isOpen={isRestoreModalOpen}
        onClose={() => {
          if (!restoringId) {
            setIsRestoreModalOpen(false);
            setCustomerToRestore(null);
          }
        }}
        title="Restore Customer"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-700">Are you sure you want to restore this customer?</p>
          <div className="flex gap-3 justify-end">
            <Button
              variant="secondary"
              onClick={() => {
                setIsRestoreModalOpen(false);
                setCustomerToRestore(null);
              }}
              disabled={!!restoringId}
            >
              Cancel
            </Button>
            <Button variant="primary" onClick={handleConfirmRestore} disabled={!!restoringId}>
              {restoringId ? 'Restoring...' : 'Restore Customer'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
